import { useEffect, useMemo, useState } from 'react';

import patternValidators from 'helpers/validators/pattern-validators';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  Checkbox,
  Input,
  InputGroup,
  MultipleSelect,
  Textarea,
} from 'components/FormComponents';

import { useGetCompanyUsersQuery } from 'api/company-api';

const isValidEmail = email => patternValidators.email.pattern.value.test(email.trim());

export const DeferralModal = ({
  visible,
  currentValue = null,
  companyId = null,
  defaultRecipientUserId = null,
  onClose = () => {},
  onSave = () => {},
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [notifyClient, setNotifyClient] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [toEmails, setToEmails] = useState([]);
  const [manualEmail, setManualEmail] = useState('');
  const [manualError, setManualError] = useState('');
  const [defaultApplied, setDefaultApplied] = useState(false);

  const { data: usersData } = useGetCompanyUsersQuery(
    { id: companyId, params: { records: 1000 } },
    { skip: !visible || !companyId || !notifyClient },
  );

  const companyUsers = useMemo(() => {
    const users = usersData?.result?.users || [];
    return users.filter(user => !!user?.email);
  }, [usersData]);

  const defaultRecipientEmail = useMemo(() => {
    if (defaultRecipientUserId == null) return '';
    const user = companyUsers.find(u => Number(u.id) === Number(defaultRecipientUserId));
    return user?.email || '';
  }, [companyUsers, defaultRecipientUserId]);

  useEffect(() => {
    if (visible) {
      setDate(currentValue ? moment(currentValue).format('YYYY-MM-DD') : '');
      setNote('');
      setNotifyClient(false);
      setEmailMessage('');
      setToEmails([]);
      setManualEmail('');
      setManualError('');
      setDefaultApplied(false);
    }
  }, [visible, currentValue, defaultRecipientUserId]);

  useEffect(() => {
    if (!notifyClient || defaultApplied) return;
    if (defaultRecipientEmail) {
      setToEmails(prev => {
        const alreadyIncluded = prev.some(
          email => email.toLowerCase() === defaultRecipientEmail.toLowerCase(),
        );
        if (alreadyIncluded) return prev;
        if (prev.length === 0) return [defaultRecipientEmail];
        return [defaultRecipientEmail, ...prev];
      });
      setDefaultApplied(true);
    } else if (companyUsers.length) {
      setDefaultApplied(true);
    }
  }, [notifyClient, defaultApplied, defaultRecipientEmail, companyUsers.length]);

  const normalizedCurrentValue = currentValue ? moment(currentValue).format('YYYY-MM-DD') : '';
  const dateUnchanged = !!date && date === normalizedCurrentValue;

  const customEmails = useMemo(() => {
    const companyEmails = new Set(
      companyUsers.map(user => user.email.toLowerCase()).filter(Boolean),
    );
    return toEmails.filter(email => !companyEmails.has(email.toLowerCase()));
  }, [toEmails, companyUsers]);

  const addManualEmail = () => {
    if (!manualEmail.trim()) return;

    if (!isValidEmail(manualEmail)) {
      setManualError(t('admin.candidate.check.deferral.invalidEmail'));
      return;
    }

    const email = manualEmail.trim();

    if (toEmails.some(existing => existing.toLowerCase() === email.toLowerCase())) {
      setManualEmail('');
      setManualError('');
      return;
    }

    setToEmails(prev => [...prev, email]);
    setManualEmail('');
    setManualError('');
  };

  const handleSave = () => {
    const emails = toEmails.map(email => email.trim()).filter(Boolean);
    const payload = {
      deferred_until: date || null,
      reason: note.trim() || null,
      notify: notifyClient && !dateUnchanged,
    };
    if (payload.notify) {
      payload.email_message = emailMessage.trim() || null;
      payload.recipient_emails = emails;
    }
    onSave(payload);
  };

  const handleClear = () => {
    resetNotifyFields();
    onSave({ deferred_until: null, reason: null, notify: false });
  };

  const resetNotifyFields = () => {
    setNotifyClient(false);
    setEmailMessage('');
    setToEmails([]);
    setManualEmail('');
    setManualError('');
    setDefaultApplied(false);
  };

  const minDate = moment().format('YYYY-MM-DD');

  useEffect(() => {
    if (dateUnchanged && notifyClient) {
      resetNotifyFields();
    }
  }, [dateUnchanged, notifyClient, defaultRecipientUserId]);

  const recipientValid = toEmails.length > 0 && toEmails.every(email => isValidEmail(email));

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={t('admin.candidate.check.deferral.title')}
      small>
      <div className="d-flex flex-column u-gap-medium">
        <InputGroup title={t('admin.candidate.check.deferredUntil')}>
          <Input
            type="date"
            name="deferred_until"
            inputProps={{
              value: date,
              min: minDate,
              onChange: e => {
                const nextDate = e.target.value;
                setDate(nextDate);
                if (!nextDate) {
                  setNote('');
                  resetNotifyFields();
                } else if (normalizedCurrentValue && nextDate === normalizedCurrentValue) {
                  setNote('');
                  resetNotifyFields();
                }
              },
            }}
          />
        </InputGroup>
        {!!date && !dateUnchanged && (
          <>
            <InputGroup title={t('admin.candidate.check.deferral.reason')} optional>
              <Textarea
                name="note"
                placeholder={t('admin.candidate.check.deferral.reasonPlaceholder')}
                rows={3}
                maxLength={255}
                inputProps={{
                  value: note,
                  onChange: e => setNote(e.target.value),
                }}
              />
            </InputGroup>
            <Checkbox
              name="notify_client"
              checked={notifyClient}
              label={t('admin.candidate.check.deferral.notifyClient')}
              onChange={checked => {
                setNotifyClient(checked);
                if (!checked) {
                  setEmailMessage('');
                  setToEmails([]);
                  setManualEmail('');
                  setManualError('');
                  setDefaultApplied(false);
                }
              }}
            />
            {notifyClient && (
              <>
                <InputGroup
                  title={t('admin.candidate.check.deferral.recipient')}
                  description={t('admin.candidate.check.deferral.recipientHint')}>
                  <MultipleSelect
                    value={toEmails}
                    onChange={setToEmails}
                    placeholder={t('admin.candidate.check.deferral.recipientPlaceholder')}
                    useSearch>
                    {companyUsers.map(user => (
                      <MultipleSelect.Item
                        key={user.id}
                        value={user.email}
                        active={toEmails.some(
                          email => email.toLowerCase() === user.email.toLowerCase(),
                        )}>
                        {`${user.first_name} ${user.last_name} • ${user.email}`}
                      </MultipleSelect.Item>
                    ))}
                    {customEmails.map(email => (
                      <MultipleSelect.Item key={email} value={email} active>
                        {email}
                      </MultipleSelect.Item>
                    ))}
                  </MultipleSelect>
                  <div className="d-flex u-gap u-margin-top--small">
                    <Input
                      type="email"
                      name="manual_email"
                      placeholder={t('admin.candidate.check.deferral.manualEmailPlaceholder')}
                      error={manualError}
                      inputProps={{
                        value: manualEmail,
                        onChange: e => {
                          setManualEmail(e.target.value);
                          if (manualError) setManualError('');
                        },
                        onKeyDown: e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addManualEmail();
                          }
                        },
                      }}
                    />
                    <Button type="secondary" onClick={addManualEmail}>
                      {t('admin.candidate.check.deferral.addEmail')}
                    </Button>
                  </div>
                </InputGroup>
                <InputGroup title={t('admin.candidate.check.deferral.emailMessage')} optional>
                  <Textarea
                    name="email_message"
                    placeholder={t('admin.candidate.check.deferral.emailMessagePlaceholder')}
                    rows={4}
                    inputProps={{
                      value: emailMessage,
                      onChange: e => setEmailMessage(e.target.value),
                    }}
                  />
                </InputGroup>
              </>
            )}
          </>
        )}
        <div className="d-flex u-gap justify-content-end u-margin-top--small">
          {dateUnchanged && (
            <Button type="delete" onClick={handleClear}>
              {t('admin.candidate.check.deferral.clear')}
            </Button>
          )}
          <Button type="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!date || dateUnchanged || (notifyClient && !recipientValid)}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </AnimatedModal>
  );
};
